import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { courseLabel } from "./courses";

function fmt(val) {
  if (val == null) return "";
  if (val instanceof Date) return val.toLocaleString();
  return String(val);
}

/**
 * Download an Excel (.xlsx) file from an array of row objects.
 * @param {object[]} rows  - flat objects where keys become column headers
 * @param {string}   filename - without extension
 */
export function downloadExcel(rows, filename) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Export");
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

/**
 * Download a PDF file from an array of row objects.
 * @param {object[]} rows     - flat objects where keys become column headers
 * @param {string}   filename - without extension
 * @param {string}   title    - heading shown in the PDF
 */
export function downloadPDF(rows, filename, title) {
  const doc = new jsPDF({ orientation: "landscape" });

  doc.setFontSize(14);
  doc.text(title, 14, 15);
  doc.setFontSize(9);
  doc.text(`Exported: ${new Date().toLocaleString()}`, 14, 22);

  if (rows.length === 0) {
    doc.text("No records found.", 14, 32);
    doc.save(`${filename}.pdf`);
    return;
  }

  const columns = Object.keys(rows[0]);
  const body = rows.map((r) => columns.map((c) => fmt(r[c])));

  autoTable(doc, {
    startY: 27,
    head: [columns],
    body,
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [41, 98, 255], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 247, 255] },
    margin: { left: 14, right: 14 },
  });

  doc.save(`${filename}.pdf`);
}

/* ─── Domain-specific row builders ──────────────────────────────────────── */

export function webinarRegistrationsToRows(registrations) {
  return registrations.map((r) => ({
    Name: r.name || "",
    Email: r.email || "",
    Phone: r.phone || "",
    Location: r.location || "",
    Course: courseLabel(r.courseSlug),
    "Tech Journey": r.journey || "",
    "Biggest Frustration": r.frustration || "",
    "Readiness to Act": r.readiness || "",
    Status: r.stage || "registered",
    "Submitted At": r.submittedAt ? new Date(r.submittedAt).toLocaleString() : "",
  }));
}

export function bootcampApplicationsToRows(applications) {
  return applications.map((a) => ({
    Name: a.name || "",
    Email: a.email || "",
    Phone: a.phone || "",
    Bootcamp: a.bootcamp?.title || a.bootcampId || "",
    Education: a.education || "",
    Experience: a.experience || "",
    Motivation: a.motivation || "",
    Status: a.status || "pending",
    "Payment Status": a.paymentStatus || "",
    "Amount Paid": a.amountPaid != null ? a.amountPaid : "",
    Currency: a.currency || "",
    "Applied At": a.createdAt ? new Date(a.createdAt).toLocaleString() : "",
  }));
}

export function landingApplicationsToRows(applications) {
  return applications.map((a) => ({
    Name: a.name || "",
    Email: a.email || "",
    Phone: a.phone || "",
    City: a.city || "",
    Region: a.region || "",
    Course: courseLabel(a.courseSlug),
    Ecosystem: a.ecosystem || "",
    "Payment Preference": a.paymentPreference || "",
    Qualified: a.qualified === true ? "Yes" : a.qualified === false ? "No" : "",
    Stage: a.stage || "booking",
    "Submitted At": a.submittedAt ? new Date(a.submittedAt).toLocaleString() : "",
  }));
}

const WAT = "Africa/Lagos";

function formatInZone(value, timeZone) {
  if (!value) return "";
  const options = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };
  try {
    return new Date(value).toLocaleString("en-GB", { ...options, timeZone: timeZone || WAT });
  } catch {
    return new Date(value).toLocaleString("en-GB", options);
  }
}

export function clarityCallBookingsToRows(bookings) {
  return bookings.map((b) => ({
    Name: b.name || "",
    Email: b.email || "",
    Phone: b.phone || "",
    Country: b.country || "",
    Course: courseLabel(b.courseSlug),
    Level: b.level || "",
    Goal: b.goal || "",
    Status: b.status || "",
    "Call Start (WAT)": formatInZone(b.startDateTime, WAT),
    "Call Start (Visitor)": b.visitorTimeZone
      ? formatInZone(b.startDateTime, b.visitorTimeZone)
      : "",
    "Visitor Time Zone": b.visitorTimeZone || "",
    "Booked At": b.createdAt ? new Date(b.createdAt).toLocaleString() : "",
    "From Webinar Registration": b.sourceWebinarRegistrationId ? "Yes" : "No",
    Rescheduled: b.rescheduleCount || 0,
    "Cancelled By": b.cancelledBy || "",
  }));
}

export function consultationsToRows(consultations) {
  return consultations.map((c) => ({
    Name: c.name || "",
    Email: c.email || "",
    Phone: c.phone || "",
    Company: c.company || "",
    Service: c.service || "",
    "Preferred Date": c.preferredDate || "",
    "Preferred Time": c.preferredTime || "",
    Message: c.message || "",
    Status: c.status || "",
    Notes: c.notes || "",
    "Submitted At": c.createdAt ? new Date(c.createdAt).toLocaleString() : "",
  }));
}

export function contactSubmissionsToRows(submissions) {
  return submissions.map((s) => ({
    Name: s.name || "",
    Email: s.email || "",
    Phone: s.phone || "",
    Subject: s.subject || "",
    Message: s.message || "",
    Status: s.status || "",
    "Submitted At": s.createdAt ? new Date(s.createdAt).toLocaleString() : "",
  }));
}

export function newsletterSubscribersToRows(subscribers) {
  return subscribers.map((s) => ({
    Email: s.email || "",
    Status: s.subscribed === false ? "Unsubscribed" : "Subscribed",
    "Subscribed At": s.subscribedAt ? new Date(s.subscribedAt).toLocaleString() : "",
    "Unsubscribed At": s.unsubscribedAt ? new Date(s.unsubscribedAt).toLocaleString() : "",
  }));
}
